/**
 * Custom Component Detector
 *
 * Detects non-OOTB adapters and transports in GeoEvent configurations.
 */

import type {
  GeoEventConfiguration,
  CustomComponentAnalysis,
  CustomComponentInfo,
} from '../types/index.js';

/**
 * Known OOTB adapter URI prefixes
 */
const OOTB_ADAPTER_PREFIXES = [
  'com.esri.ges.adapter.inbound/',
  'com.esri.ges.adapter.outbound/',
];

/**
 * Known OOTB transport URI prefixes
 */
const OOTB_TRANSPORT_PREFIXES = [
  'com.esri.ges.transport.inbound/',
  'com.esri.ges.transport.outbound/',
];

/**
 * Known OOTB processor URI prefixes
 */
const OOTB_PROCESSOR_PREFIXES = ['com.esri.ges.processor/', 'com.esri.geoevent.processor/'];

/** Esri-built Gallery components installed separately from the core product. */
const ESRI_ADDON_CONNECTORS = new Set([
  'receive-flightaware-connector', 'geotab-in', 'esri-in-gtfsrealtime',
  'receive-json-mqtt-in', 'rabbitmq-text-in', 'verizonconnect-in',
  'receive-waze-connector', 'Networkfleet', 'ais_tcp', 'http-cap-in',
  'cityworks-in', 'esri-receive-taip-udp', 'esri-receive-taip-tcp',
  'receive-cot-http-in', 'receive-cot-tcp-in', 'push-json-mqtt-out',
  'rabbitmq-text-out', 'esri-out-json-fs-add-nonnull',
  'esri-out-json-fs-update-nonnull', 'cityworks-out', 'send-cot-tcp-out',
]);

/**
 * Checks if a URI is an OOTB adapter
 */
function isOotbAdapter(uri: string): boolean {
  return OOTB_ADAPTER_PREFIXES.some((prefix) => uri.startsWith(prefix));
}

/**
 * Checks if a URI is an OOTB transport
 */
function isOotbTransport(uri: string): boolean {
  return OOTB_TRANSPORT_PREFIXES.some((prefix) => uri.startsWith(prefix));
}

/**
 * Checks if a URI is an OOTB processor
 */
export function isOotbProcessor(uri: string): boolean {
  return OOTB_PROCESSOR_PREFIXES.some((prefix) => uri.startsWith(prefix));
}

/**
 * Detects custom (non-OOTB) components in a configuration
 */
export function detectCustomComponents(config: GeoEventConfiguration): CustomComponentAnalysis {
  const customAdapters: CustomComponentInfo[] = [];
  const customTransports: CustomComponentInfo[] = [];
  const esriAddonAdapters: CustomComponentInfo[] = [];
  const esriAddonTransports: CustomComponentInfo[] = [];

  // Track unique URIs to avoid duplicates
  const seenAdapterUris = new Set<string>();
  const seenTransportUris = new Set<string>();
  const seenEsriAddonAdapterUris = new Set<string>();
  const seenEsriAddonTransportUris = new Set<string>();

  const addComponent = (
    components: CustomComponentInfo[],
    seenUris: Set<string>,
    uri: string,
    type: CustomComponentInfo['type'],
    direction: CustomComponentInfo['direction'],
    usedBy: string,
    properties: CustomComponentInfo['properties'],
  ): void => {
    if (!seenUris.has(uri)) {
      seenUris.add(uri);
      components.push({ uri, type, direction, usedBy: [usedBy], properties });
      return;
    }
    components.find((component) => component.uri === uri)?.usedBy.push(usedBy);
  };

  const inspectComponent = (
    connector: string,
    uri: string,
    type: CustomComponentInfo['type'],
    direction: CustomComponentInfo['direction'],
    usedBy: string,
    properties: CustomComponentInfo['properties'],
    isOotb: boolean,
  ): void => {
    if (ESRI_ADDON_CONNECTORS.has(connector)) {
      addComponent(
        type === 'adapter' ? esriAddonAdapters : esriAddonTransports,
        type === 'adapter' ? seenEsriAddonAdapterUris : seenEsriAddonTransportUris,
        uri, type, direction, usedBy, properties,
      );
    } else if (uri && !isOotb) {
      addComponent(
        type === 'adapter' ? customAdapters : customTransports,
        type === 'adapter' ? seenAdapterUris : seenTransportUris,
        uri, type, direction, usedBy, properties,
      );
    }
  };

  // Check inputs
  for (const input of config.inputs) {
    const usedBy = input.label || input.name;
    inspectComponent(input.connector, input.adapter.uri, 'adapter', 'inbound', usedBy, input.adapter.properties, isOotbAdapter(input.adapter.uri));
    inspectComponent(input.connector, input.transport.uri, 'transport', 'inbound', usedBy, input.transport.properties, isOotbTransport(input.transport.uri));
  }

  // Check outputs
  for (const output of config.outputs) {
    const usedBy = output.label || output.name;
    inspectComponent(output.connector, output.adapter.uri, 'adapter', 'outbound', usedBy, output.adapter.properties, isOotbAdapter(output.adapter.uri));
    inspectComponent(output.connector, output.transport.uri, 'transport', 'outbound', usedBy, output.transport.properties, isOotbTransport(output.transport.uri));
  }

  return {
    hasCustomComponents: customAdapters.length > 0 || customTransports.length > 0,
    customAdapters,
    customTransports,
    hasEsriAddons: esriAddonAdapters.length > 0 || esriAddonTransports.length > 0,
    esriAddonAdapters,
    esriAddonTransports,
  };
}
